import { useCallback } from "react";
import { useUser } from "src/contexts/user-context";
import {
  type AdStudioStyleBrandProfileOptionsQuery,
  type StyleEntitiesQuery,
  useAdStudioStyleBrandProfileOptionsQuery,
  useStyleEntitiesQuery,
} from "src/graphql/types";
import { selectAdSurfaceStyleOptions } from "src/pages/ad-studio/components/ad-studio-style-select";
import { getPreferredAdStudioBrandProfileId } from "src/pages/ad-studio/hooks/ad-studio-brand-profile";

export type AdSurfaceStyle = { id: string; name: string };

const NO_STYLES: AdSurfaceStyle[] = [];

// Module-level so React Query's `select` stays referentially stable.
const selectProfiles = (data: AdStudioStyleBrandProfileOptionsQuery) =>
  data.brand_profiles;

/** Ordered as `AdStudioStyleSelect` lists them. */
export function useAdSurfaceStyles(): {
  styles: AdSurfaceStyle[];
  isLoading: boolean;
} {
  const { workspace } = useUser();
  const { data: profiles, isLoading: isLoadingProfiles } =
    useAdStudioStyleBrandProfileOptionsQuery(undefined, {
      select: selectProfiles,
    });

  // BrandProfileSelect's default: the profile the chat will open on.
  const brandProfileId = getPreferredAdStudioBrandProfileId(
    profiles,
    workspace?.id,
  );

  const selectStyles = useCallback(
    (data: StyleEntitiesQuery): AdSurfaceStyle[] =>
      selectAdSurfaceStyleOptions(data.styleEntities, brandProfileId ?? "").map(
        (style) => ({ id: style.id, name: style.name }),
      ),
    [brandProfileId],
  );

  const { data: styles, isLoading: isLoadingStyles } = useStyleEntitiesQuery(
    undefined,
    {
      enabled: Boolean(brandProfileId),
      select: selectStyles,
    },
  );

  // A disabled query reports idle rather than loading, so a workspace with no
  // brand profile settles on an empty list instead of loading forever.
  return {
    styles: styles ?? NO_STYLES,
    isLoading: isLoadingProfiles || isLoadingStyles,
  };
}
